import type { Breakpoint } from '../components/spacing/types.js';
import { useMediaQuery, type UseMediaQueryOptions } from './use-media-query.js';

type BreakpointDefinition = {
  minWidth?: number;
  maxWidth?: number;
};

const breakpoints: Record<Breakpoint, BreakpointDefinition> = {
  sm: { maxWidth: 639 },
  md: { minWidth: 640, maxWidth: 1023 },
  lg: { minWidth: 1024, maxWidth: 1439 },
  xl: { minWidth: 1440 },
};

export type BreakpointRange = {
  min?: Breakpoint;
  max?: Breakpoint;
};

function buildBreakpointQuery({ min, max }: BreakpointRange): string {
  if (min === undefined && max === undefined) {
    throw new Error('useBreakpoint requires at least one of "min" or "max".');
  }

  const parts: string[] = [];

  if (min !== undefined) {
    const minWidth = breakpoints[min].minWidth;

    if (minWidth === undefined) {
      throw new Error(`Breakpoint "${min}" does not have a min width.`);
    }

    parts.push(`(min-width: ${minWidth}px)`);
  }

  if (max !== undefined) {
    const maxWidth = breakpoints[max].maxWidth;

    if (maxWidth === undefined) {
      throw new Error(`Breakpoint "${max}" does not have a max width.`);
    }

    parts.push(`(max-width: ${maxWidth}px)`);
  }

  return parts.join(' and ');
}

export function useBreakpoint(range: BreakpointRange, options: UseMediaQueryOptions = {}): boolean {
  return useMediaQuery(buildBreakpointQuery(range), options);
}

export function matchesBreakpoint(range: BreakpointRange): boolean {
  return window.matchMedia(buildBreakpointQuery(range)).matches;
}
